import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { formatCurrency } from '../../../shared/utils/formatCurrency';

// The collector's in-round COLLECTION-PROGRESS view — the emotional peak (receiving
// the pot). Rendered whenever it's the member's turn to collect, in BOTH money-flow
// models. Read-only progress; the direct-model confirm/reject actions live in
// CollectorPaymentsToMe below this card. All money is exact + server-derived from
// get_collector_round_payments (Stage 43: amount_expected + amount_received).
//
// Excludes the collector's OWN hand (collector_hand_id) — they never pay themselves,
// so we never show them "waiting on" themselves.
//
// COPY: FRANNIE DRAFT — warm, reassuring, good-news-arriving. Not a cold status table.

const STATUS = {
  paid:             { key: 'confirmed', label: 'Confirmed',              dot: 'bg-success-500', text: 'text-success-700' },
  member_submitted: { key: 'awaiting',  label: 'Awaiting your confirmation', dot: 'bg-warning-500', text: 'text-warning-700' },
  pending:          { key: 'pending',   label: 'Not yet paid',          dot: 'bg-neutral-300', text: 'text-neutral-500' },
};
const bucketOf = (s) => STATUS[s]?.key || 'pending';

const CollectorCollectionProgress = ({ groupId, collectorHandId, isDirect, onJumpToConfirm, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRows = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    const { data } = await supabaseClient.rpc('get_collector_round_payments', { p_group_id: groupId });
    // Exclude the collector's own hand — the pot is what OTHERS pay in.
    setRows((data || []).filter(r => String(r.hand_id) !== String(collectorHandId)));
    setLoading(false); setRefreshing(false);
  }, [groupId, collectorHandId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  // Refetch after an in-page confirm (parent bumps refreshKey) — silent, no spinner.
  useEffect(() => { if (refreshKey) fetchRows({ silent: true }); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Refresh when the tab/window regains focus, so the pot fills without a manual poke.
  useEffect(() => {
    const onActive = () => { if (document.visibilityState === 'visible') fetchRows({ silent: true }); };
    window.addEventListener('focus', onActive);
    document.addEventListener('visibilitychange', onActive);
    return () => { window.removeEventListener('focus', onActive); document.removeEventListener('visibilitychange', onActive); };
  }, [fetchRows]);

  const total = rows.length;
  const paidCount = rows.filter(r => r.status === 'paid').length;
  const pot = rows.reduce((s, r) => s + Number(r.amount_expected || 0), 0);
  const collected = rows.reduce((s, r) => s + Number(r.amount_received || 0), 0);
  const pct = pot > 0 ? Math.min(100, Math.round((collected / pot) * 100)) : 0;
  const allIn = total > 0 && paidCount === total;
  const awaiting = rows.filter(r => r.status === 'member_submitted');

  const groups = [
    { key: 'confirmed', label: 'Confirmed', rows: rows.filter(r => bucketOf(r.status) === 'confirmed') },
    { key: 'awaiting',  label: isDirect ? 'Awaiting your confirmation' : 'Awaiting confirmation',
      rows: rows.filter(r => bucketOf(r.status) === 'awaiting') },
    { key: 'pending',   label: 'Not yet paid', rows: rows.filter(r => bucketOf(r.status) === 'pending') },
  ];

  if (loading) {
    return (
      <div className="bg-neutral-100 rounded-lg shadow-sm border border-primary-300 p-5 mb-6">
        <p className="text-sm text-neutral-400">Loading your collection…</p>
      </div>
    );
  }

  // Standard dark card surface (bg-neutral-100 == #1E293B), like every other
  // MemberDashboard card. The "special moment" comes from the gold accent border
  // + the 🎉 + the progress bar — NOT a light background.
  return (
    <div className="bg-neutral-100 rounded-lg shadow-sm border-2 border-primary-500 p-5 mb-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          {/* FRANNIE DRAFT — celebratory header */}
          <h3 className="text-base font-bold text-neutral-900">🎉 It's your turn — your pot is filling up</h3>
          <p className="text-sm text-neutral-600">
            {/* FRANNIE DRAFT */}
            {isDirect
              ? 'Members send their contributions to you and confirm below.'
              : 'Your group admin collects everyone\'s contributions and pays you.'}
          </p>
        </div>
        <button
          onClick={() => fetchRows({ silent: true })}
          disabled={refreshing}
          className="flex-shrink-0 text-xs text-primary-700 hover:text-primary-800 underline disabled:opacity-50"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Headline numbers — FRANNIE DRAFT */}
      <div className="mt-3 mb-2">
        <p className="text-2xl font-bold text-neutral-900">
          {allIn
            ? `🎊 Everyone's paid — ${formatCurrency(collected)} is yours!`
            : `${paidCount} of ${total} have paid you`}
        </p>
        <p className="text-sm text-neutral-700 mt-0.5">
          <span className="font-semibold text-success-700">{formatCurrency(collected)}</span> of {formatCurrency(pot)} collected
        </p>
      </div>

      {/* Progress bar */}
      <div className="h-2.5 w-full rounded-full bg-neutral-200 overflow-hidden mb-4" aria-hidden="true">
        <div className="h-full rounded-full bg-success-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Direct model: nudge to confirm what's waiting — FRANNIE DRAFT */}
      {isDirect && awaiting.length > 0 && (
        <button
          onClick={onJumpToConfirm}
          className="w-full mb-4 px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-primary-700"
        >
          {awaiting.length === 1
            ? '1 payment is waiting for you to confirm →'
            : `${awaiting.length} payments are waiting for you to confirm →`}
        </button>
      )}

      {/* Roster grouped by status */}
      <div className="space-y-3">
        {groups.map(g => g.rows.length > 0 && (
          <div key={g.key}>
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">{g.label} ({g.rows.length})</p>
            <div className="space-y-1">
              {g.rows.map(r => {
                const s = STATUS[r.status] || STATUS.pending;
                return (
                  <div key={r.payment_id ?? `${r.hand_id}`} className="flex items-center justify-between py-1.5 px-2 rounded-md bg-neutral-50">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                      <span className="text-sm text-neutral-900 truncate">{r.member_name || 'Member'}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm text-neutral-600">
                        {formatCurrency(r.amount_received ?? r.submitted_amount ?? r.amount_expected)}
                      </span>
                      <span className={`text-xs font-medium ${s.text}`}>{s.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CollectorCollectionProgress;
